/**
 * The job page's master tabs and the sub tabs under each (D-083): a pure table
 * and the functions over it. A master either holds sub tabs (Project
 * Management: Details — the key is `billing` — Schedule of values, …) or is
 * one tab itself (General — its Notes and Details sub tabs gave way to one
 * page, the Fields card above the notes, design 46 — To-dos, Line items,
 * Purchase orders, Files). The order is Dakota's of
 * 2026-10-12 (D-147): General, Checklists, Line items, Purchase orders,
 * Project Management, Scheduling, Files. The URL carries the master and the
 * sub tab — `/jobs/7739/scheduling/visits` — or just the master when it has no
 * sub tabs — `/jobs/7739/handoff`. The API knows nothing of masters; it serves
 * a tab by its key (`JOB_TABS` in shared). Accounting (Labor, Financials) left
 * the page on 2026-10-05 (D-093): its two keys are `HIDDEN_TABS` — still served
 * by the API, shown on no screen, their old addresses redirected to General.
 */

import type { JobTab } from '@unitedos/shared';
import { JOB_TABS } from '@unitedos/shared';

export const JOB_MASTERS = ['general', 'to-dos', 'line-items', 'purchase-orders', 'project-management', 'scheduling', 'documents'] as const;
export type JobMaster = (typeof JOB_MASTERS)[number];

/** Tab keys the API serves that no master shows (D-093). */
export const HIDDEN_TABS = ['labor', 'financials'] as const satisfies readonly JobTab[];
export type HiddenTab = (typeof HIDDEN_TABS)[number];
/** A tab the page can show. */
export type ShownTab = Exclude<JobTab, HiddenTab>;
/** The master whose address still arrives from before (D-093); it lands on General. */
const REMOVED_MASTER = 'accounting';

export const MASTER_LABELS: Readonly<Record<JobMaster, string>> = {
  general: 'General',
  // Its own master since 2026-10-12 (D-147); the checklists became to-dos on 2026-10-23 (design 39), the address with them.
  'to-dos': 'To-dos',
  'line-items': 'Line items',
  // Costs & POs since design 47 (D-300): both kinds under one master; the address stays (D-147).
  'purchase-orders': 'Costs & POs',
  documents: 'Files',
  'project-management': 'Project Management',
  scheduling: 'Scheduling',
};

/** Each master's tabs, in the order the row shows them. A master with one tab named as itself has no sub-tab row. */
const MASTER_TABS: Readonly<Record<JobMaster, readonly ShownTab[]>> = {
  // One page since design 46 (2026-11-02): the cards, the Fields card, the notes.
  general: ['general'],
  'to-dos': ['to-dos'],
  'line-items': ['line-items'],
  // Its own master, beside Line items (Dakota, 2026-10-06, after D-105 first placed it under Project Management).
  'purchase-orders': ['purchase-orders'],
  documents: ['documents'],
  // Details first (Dakota, 2026-10-08): the job's billing data, which sat above the schedule-of-values grid until then.
  'project-management': ['billing', 'schedule-of-values', 'plan', 'change-orders'],
  scheduling: ['visits', 'delivery'],
};

/** Tab keys the page answered to before, each mapped to today's key: General's two sub tabs became General itself (design 46). */
const LEGACY_TABS: Readonly<Record<string, JobTab>> = { schedule: 'visits', handoff: 'to-dos', communications: 'general', details: 'general' };

export function isJobMaster(value: string | undefined): value is JobMaster {
  return value !== undefined && (JOB_MASTERS as readonly string[]).includes(value);
}

export function isJobTab(value: string | undefined): value is JobTab {
  return value !== undefined && (JOB_TABS as readonly string[]).includes(value);
}

export function isShownTab(value: string | undefined): value is ShownTab {
  return isJobTab(value) && !(HIDDEN_TABS as readonly string[]).includes(value);
}

export function tabsOf(master: JobMaster): readonly ShownTab[] {
  return MASTER_TABS[master];
}

/** A master that is one tab of its own (General, To-dos, Line items, Purchase orders, Files): no sub-tab row, a one-segment address. */
export function isSoloMaster(master: JobMaster): boolean {
  const tabs = MASTER_TABS[master];
  return tabs.length === 1 && tabs[0] === master;
}

/** The tab a master opens on: its first. Every master has one; the test holds it. */
export function firstTabOf(master: JobMaster): ShownTab {
  const first = MASTER_TABS[master][0];
  if (first === undefined) throw new Error(`job master ${master} has no tabs`);
  return first;
}

/** The master a tab belongs to. Every tab is in exactly one; the test holds it. */
export function masterOf(tab: ShownTab): JobMaster {
  const master = JOB_MASTERS.find((m) => MASTER_TABS[m].includes(tab));
  if (master === undefined) throw new Error(`job tab ${tab} belongs to no master`);
  return master;
}

/** The address of a tab: `/jobs/N/master/tab`, or `/jobs/N/master` for a master that is its own one tab. */
export function jobHref(jobNumber: number | string, master: JobMaster, tab: ShownTab = firstTabOf(master)): string {
  return isSoloMaster(master) ? `/jobs/${jobNumber}/${master}` : `/jobs/${jobNumber}/${master}/${tab}`;
}

export type JobAddress = { readonly kind: 'page'; readonly master: JobMaster; readonly tab: ShownTab } | { readonly kind: 'redirect'; readonly to: string } | { readonly kind: 'missing' };

/**
 * What an address means, for every shape the router hands over:
 * - `/jobs/N` → General's first tab (a redirect);
 * - `/jobs/N/x` → the page when `x` is a master of its own (General, To-dos, Line items, Files); a redirect to
 *   the tab's master when `x` is a tab key the page answered to before (`schedule`, `details` translated first);
 * - `/jobs/N/m/t` → the page when the table holds the pair; a redirect when `t` is a tab the
 *   masters moved (D-076's `general/line-items`, D-147's `project-management/handoff`, design 46's
 *   `general/details` and `general/communications`), or `m` is a master whose address has one segment;
 * - a hidden tab's address, or the removed Accounting master's, lands on General (D-093);
 * - anything else is the missing page.
 */
export function resolveJobAddress(jobNumber: string | undefined, first: string | undefined, second: string | undefined): JobAddress {
  if (jobNumber === undefined) return { kind: 'missing' };
  if (first === undefined) return { kind: 'redirect', to: jobHref(jobNumber, 'general') };
  if (second === undefined) {
    if (isJobMaster(first) && isSoloMaster(first)) return { kind: 'page', master: first, tab: firstTabOf(first) };
    if (isJobMaster(first)) return { kind: 'redirect', to: jobHref(jobNumber, first) };
    if (first === REMOVED_MASTER) return { kind: 'redirect', to: jobHref(jobNumber, 'general') };
    const key = LEGACY_TABS[first] ?? first;
    if (!isJobTab(key)) return { kind: 'missing' };
    if (!isShownTab(key)) return { kind: 'redirect', to: jobHref(jobNumber, 'general') };
    return { kind: 'redirect', to: jobHref(jobNumber, masterOf(key), key) };
  }
  if (!isJobMaster(first) && first !== REMOVED_MASTER) return { kind: 'missing' };
  const tab = LEGACY_TABS[second] ?? second;
  if (!isJobTab(tab)) return { kind: 'missing' };
  if (!isShownTab(tab)) return { kind: 'redirect', to: jobHref(jobNumber, 'general') };
  if (isJobMaster(first) && MASTER_TABS[first].includes(tab) && !isSoloMaster(first) && tab === second) return { kind: 'page', master: first, tab };
  // A pair the table no longer holds (or a renamed tab), but a real tab: where it lives now, by its name today.
  return { kind: 'redirect', to: jobHref(jobNumber, masterOf(tab), tab) };
}
